import React, { useEffect, useRef, useState } from 'react';
import { api } from '../utils/api';

/**
 * EnrollmentDocuments — list + upload UI for the documents attached to a
 * single enrollment. Uses the unified S3 file service:
 *   1. POST  /api/files/upload?category=documents   → file_id
 *   2. POST  /api/enrollments/{id}/documents        → adds {file_id, type}
 * The backend regenerates a presigned download URL on every read.
 */

const DOCUMENT_TYPES = [
  { v: 'birth_certificate', label: 'Birth certificate' },
  { v: 'photo_id', label: 'Photo ID' },
  { v: 'medical_form', label: 'Medical / GP form' },
  { v: 'immunization_record', label: 'Immunisation record' },
  { v: 'proof_of_address', label: 'Proof of address' },
  { v: 'other', label: 'Other' },
];

const fmtBytes = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const friendlyTypeName = (raw) =>
  DOCUMENT_TYPES.find((d) => d.v === raw)?.label || raw || 'Document';

export function EnrollmentDocuments({ enrollment, onChanged, readOnly = false }) {
  const [docs, setDocs] = useState(enrollment.documents || []);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [docType, setDocType] = useState('birth_certificate');
  const fileInputRef = useRef(null);

  useEffect(() => { setDocs(enrollment.documents || []); }, [enrollment]);

  const handleSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      // Step 1 — upload to object storage (S3 via presigned PUT) so the
      // backend never has to babysit a giant base64 payload in JSON.
      const uploaded = await api.uploadFile(file, {
        category: 'documents',
        linkedType: 'enrollment',
        linkedId: enrollment._id,
      });
      // Step 2 — attach the resulting file_id to the enrollment.
      const updated = await api.addEnrollmentDocument(enrollment._id, {
        document_type: docType,
        file_id: uploaded.file_id,
      });
      setDocs(updated.documents || []);
      onChanged?.(updated);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div data-testid={`enrollment-docs-${enrollment._id}`} style={{ marginTop: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h4 style={{ margin: 0 }}>📎 Documents <span style={{ color: 'hsl(var(--muted-foreground))', fontWeight: 400 }}>({docs.length})</span></h4>
        {!readOnly && (
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              disabled={uploading}
              data-testid={`enrollment-doc-type-${enrollment._id}`}
            >
              {DOCUMENT_TYPES.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
            </select>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleSelect}
              disabled={uploading}
              accept=".pdf,.jpg,.jpeg,.png,.heic,.webp"
              style={{ display: 'none' }}
              data-testid={`enrollment-doc-file-${enrollment._id}`}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid={`enrollment-doc-upload-${enrollment._id}`}
            >
              {uploading ? 'Uploading…' : '+ Upload'}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message" style={{ marginTop: '0.5rem' }} data-testid={`enrollment-doc-error-${enrollment._id}`}>
          {error}
        </div>
      )}

      {docs.length === 0 ? (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.75rem',
          background: 'hsl(var(--muted))',
          borderRadius: 'var(--radius)',
          fontSize: '0.85rem',
          color: 'hsl(var(--muted-foreground))',
        }}>
          No documents uploaded yet. Upload PDFs or images for your provider's records.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.5rem' }}>
          {docs.map((d, idx) => (
            <div
              key={`${d.storage_key || d.document_url || idx}`}
              data-testid={`enrollment-doc-row-${enrollment._id}-${idx}`}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: '0.5rem', flexWrap: 'wrap',
                padding: '0.55rem 0.85rem',
                background: 'hsl(var(--muted))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 'var(--radius)',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <strong>{friendlyTypeName(d.document_type)}</strong>{' '}
                {d.verified && (
                  <span
                    className="badge"
                    style={{
                      background: 'hsl(140 50% 92%)', color: 'hsl(140 50% 30%)',
                      fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: '999px',
                    }}
                  >
                    ✓ Verified
                  </span>
                )}
                <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                  {d.original_filename || 'Document'}
                  {d.size ? ` · ${fmtBytes(d.size)}` : ''}
                  {d.uploaded_at ? ` · ${new Date(d.uploaded_at).toLocaleDateString()}` : ''}
                </div>
              </div>
              {d.document_url && (
                <a
                  href={d.document_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-ghost"
                  data-testid={`enrollment-doc-view-${enrollment._id}-${idx}`}
                >
                  View
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default EnrollmentDocuments;
